import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { InlineAlert } from "@/components/notifications";
import { cn } from "@/lib/utils";
import {
  NotificationVisualMap,
  resolveNotificationVisual,
} from "@/services/notifications";
import type { NotificationEntry } from "@/stores";
import type { NotificationSeverityName } from "@/lib/tokens";

const MAX_VISIBLE_DEFAULT = 8;
const SEVERITY_ORDER: NotificationSeverityName[] = [
  "critical",
  "error",
  "warning",
  "success",
  "info",
];

interface NotificationPanelProps {
  notifications: NotificationEntry[];
  loading: boolean;
  error?: string;
  unreadCount: number;
  severityCounts: Record<NotificationSeverityName, number>;
  totalCounts?: Record<NotificationSeverityName, number>;
  onRefresh: () => Promise<void> | void;
  onMarkAsRead: (id: string) => Promise<void> | void;
  onMarkAllAsRead: () => Promise<void> | void;
  onClose?: () => void;
  onNavigate?: (notification: NotificationEntry) => void;
  lastSynced?: string;
  reducedMotion?: boolean;
  maxVisible?: number;
  autoFocus?: boolean;
  onOpenCenter?: () => void;
}

const formatRelativeTime = (timestamp: string): string => {
  const now = Date.now();
  const value = new Date(timestamp).getTime();
  const diffMinutes = Math.max(0, Math.floor((now - value) / (1000 * 60)));

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
  return `${Math.floor(diffMinutes / 1440)}d ago`;
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  loading,
  error,
  unreadCount,
  severityCounts,
  totalCounts,
  onRefresh,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
  onNavigate,
  lastSynced,
  reducedMotion = false,
  maxVisible = MAX_VISIBLE_DEFAULT,
  autoFocus = false,
  onOpenCenter,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [liveMessage, setLiveMessage] = useState<string>("Notification feed ready.");

  useEffect(() => {
    if (!autoFocus) return;
    const firstItem = itemRefs.current.find(Boolean);
    if (firstItem) {
      firstItem.focus();
      setFocusedIndex(0);
    } else {
      containerRef.current?.focus();
    }
  }, [autoFocus, notifications.length]);

  const visibleNotifications = useMemo(
    () => notifications.slice(0, maxVisible),
    [notifications, maxVisible],
  );

  useEffect(() => {
    if (notifications.length === 0) {
      setLiveMessage("Notification feed is empty.");
      return;
    }
    const unread = notifications.reduce((total, notification) => total + (notification.read ? 0 : 1), 0);
    const newest = notifications[0];
    setLiveMessage(`Notification feed updated. ${unread} unread of ${notifications.length}. Latest event: ${newest.formatted.title}.`);
  }, [notifications]);

  const highestUnreadSeverity = SEVERITY_ORDER.find(
    (severity) => (severityCounts[severity] ?? 0) > 0,
  );

  const summaryTone =
    highestUnreadSeverity ??
    (SEVERITY_ORDER.find((severity) => (totalCounts?.[severity] ?? 0) > 0) ??
      "info");

  const summaryTokens = NotificationVisualMap[summaryTone];

  const handleItemKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
    notification: NotificationEntry,
  ) => {
    const totalItems = visibleNotifications.length;
    if (!totalItems) return;

    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      const nextIndex = (index + 1) % totalItems;
      itemRefs.current[nextIndex]?.focus();
      setFocusedIndex(nextIndex);
      return;
    }

    if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      const prevIndex = (index - 1 + totalItems) % totalItems;
      itemRefs.current[prevIndex]?.focus();
      setFocusedIndex(prevIndex);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      itemRefs.current[0]?.focus();
      setFocusedIndex(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      const lastIndex = totalItems - 1;
      itemRefs.current[lastIndex]?.focus();
      setFocusedIndex(lastIndex);
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect(notification);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      onClose?.();
    }
  };

  const handleSelect = (notification: NotificationEntry) => {
    void onMarkAsRead(notification.id);
    if (notification.actionUrl) {
      onNavigate?.(notification);
    }
  };

  return (
    <div
      ref={containerRef}
      className="flex w-full flex-col bg-white text-left outline-none"
      role="menu"
      aria-label="Notifications"
      tabIndex={-1}
    >
      <p aria-live="polite" role="status" className="sr-only">
        {liveMessage}
      </p>
      <header
        className={cn(
          "flex items-center justify-between border-b px-4 py-3 sm:px-5",
          !reducedMotion && "transition-colors duration-150",
        )}
        style={{ borderColor: summaryTokens.border }}
      >
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-slate-900">Notifications</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">
              {unreadCount > 0
                ? `${unreadCount} unread`
                : "All caught up"}
            </span>
            {highestUnreadSeverity ? (
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                style={{ backgroundColor: summaryTokens.accent }}
              >
                {highestUnreadSeverity.toUpperCase()}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void onMarkAllAsRead();
              }}
              className={cn(
                "h-7 px-2 text-xs font-medium text-slate-600 hover:text-slate-900",
                !reducedMotion && "transition-colors duration-150",
              )}
              data-testid="button-mark-all-read"
            >
              Mark all read
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void onRefresh();
            }}
            className={cn(
              "h-7 px-2 text-xs font-medium text-slate-600 hover:text-slate-900",
              !reducedMotion && "transition-colors duration-150",
            )}
            data-testid="button-refresh-notifications"
          >
            Refresh
          </Button>
        </div>
      </header>

      <div
        className="max-h-[26rem] overflow-y-auto focus:outline-none"
        role="presentation"
      >
        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500">
            Loading notifications…
          </div>
        ) : error ? (
          <div className="px-6 py-12 text-center text-sm text-rose-600">
            {error}
          </div>
        ) : visibleNotifications.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500">
            You’re all caught up.
          </div>
        ) : (
          <ul className="flex flex-col gap-3 px-4 py-4">
            {visibleNotifications.map((notification, index) => {
              const visual = resolveNotificationVisual(notification.type);
              const AccentIcon = visual.icon;
              const isFocused = focusedIndex === index;
              const isUnread = !notification.read;
              const meta = notification.formatted.meta;

              return (
                <li key={notification.id} className="list-none">
                  <div
                    ref={(node) => {
                      itemRefs.current[index] = node;
                    }}
                    role="menuitem"
                    tabIndex={0}
                      aria-label={`${notification.formatted.title} – ${formatRelativeTime(notification.timestamp)}`}
                    className={cn(
                      "group w-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/50 focus-visible:ring-offset-2",
                      !reducedMotion && "transition-shadow duration-150",
                      isFocused && "ring-2 ring-brand-red/40 ring-offset-2",
                    )}
                    onKeyDown={(event) => handleItemKeyDown(event, index, notification)}
                    onClick={() => handleSelect(notification)}
                    onFocus={() => setFocusedIndex(index)}
                    data-testid={`notification-item-${index}`}
                  >
                    <InlineAlert
                      tone={notification.type}
                      layout="compact"
                      icon={
                        <AccentIcon
                          className="h-5 w-5"
                          style={{ color: visual.iconColor }}
                          aria-hidden="true"
                        />
                      }
                      title={notification.formatted.title}
                      description={notification.formatted.body}
                      className={cn(
                        "bg-white text-left shadow-sm",
                        isUnread && "border-slate-200",
                      )}
                      style={{
                        boxShadow: `inset 4px 0 0 ${visual.accent}`,
                        opacity: isUnread ? 1 : 0.82,
                      }}
                      role="presentation"
                      actions={
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                          <span>{formatRelativeTime(notification.timestamp)}</span>
                          {meta ? <span>{meta}</span> : null}
                          {notification.actionUrl ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs font-medium text-slate-600 hover:text-slate-900"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSelect(notification);
                                onNavigate?.(notification);
                              }}
                            >
                              View details
                            </Button>
                          ) : null}
                        </div>
                      }
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <footer className="border-t px-4 py-3 text-xs text-slate-500 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Last synced:{" "}
            {lastSynced ? formatRelativeTime(lastSynced) : "—"}
          </span>
          <div className="flex items-center gap-2">
            {onOpenCenter ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-slate-600 hover:text-slate-900"
                onClick={() => onOpenCenter()}
                data-testid="button-open-notification-center"
              >
                Open Notification Center
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-slate-600 hover:text-slate-900"
              onClick={() => onClose?.()}
            >
              Close
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NotificationPanel;
